import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiClient, formatApiErrorDetail } from "../lib/api";
import { AppHeader } from "../components/AppHeader";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Save, Trash2, ArrowLeft, Phone } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";

export default function CustomerForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "",
    phone: "",
    notes: "",
    last_visit: "",
    follow_up: "",
  });
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    apiClient
      .get(`/customers/${id}`)
      .then((r) =>
        setForm({
          name: r.data.name || "",
          phone: r.data.phone || "",
          notes: r.data.notes || "",
          last_visit: r.data.last_visit || "",
          follow_up: r.data.follow_up || "",
        })
      )
      .catch(() => toast.error("Customer not found"))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Name and phone are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        notes: form.notes.trim(),
        last_visit: form.last_visit || null,
        follow_up: form.follow_up || null,
      };
      if (isEdit) {
        await apiClient.put(`/customers/${id}`, payload);
        toast.success("Customer updated");
      } else {
        await apiClient.post(`/customers`, payload);
        toast.success("Customer added");
      }
      navigate("/customers");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    try {
      await apiClient.delete(`/customers/${id}`);
      toast.success("Customer deleted");
      navigate("/customers");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to delete");
    }
  };

  return (
    <>
      <AppHeader title={isEdit ? "Edit Customer" : "Add Customer"} subtitle={isEdit ? "Update details" : "Save details to follow up"} />
      <div className="px-4 pt-4 pb-28">
        <button
          onClick={() => navigate(-1)}
          data-testid="back-btn"
          className="flex items-center gap-1 text-sm font-semibold text-slate-600 mb-4"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        {loading ? (
          <div className="text-center text-slate-500 py-10">Loading...</div>
        ) : (
          <form
            onSubmit={onSubmit}
            data-testid="customer-form"
            className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-4"
          >
            <div className="space-y-1.5">
              <Label htmlFor="name">Customer Name *</Label>
              <Input
                id="name"
                data-testid="input-name"
                value={form.name}
                onChange={set("name")}
                placeholder="e.g., Suresh Sharma"
                required
                className="h-14 rounded-xl text-base"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="phone">Phone Number *</Label>
              <div className="relative">
                <Phone className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  id="phone"
                  data-testid="input-phone"
                  type="tel"
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="98765 43210"
                  required
                  className="h-14 pl-12 rounded-xl text-base"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="last_visit">Last Visit</Label>
                <Input
                  id="last_visit"
                  data-testid="input-last-visit"
                  type="date"
                  value={form.last_visit}
                  onChange={set("last_visit")}
                  className="h-14 rounded-xl text-base"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="follow_up">Follow-up</Label>
                <Input
                  id="follow_up"
                  data-testid="input-follow-up"
                  type="date"
                  value={form.follow_up}
                  onChange={set("follow_up")}
                  className="h-14 rounded-xl text-base"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                data-testid="input-notes"
                value={form.notes}
                onChange={set("notes")}
                rows={4}
                placeholder="Anything to remember (e.g., owes ₹500, prefers WhatsApp)"
                className="rounded-xl text-base"
              />
            </div>

            <Button
              type="submit"
              disabled={saving}
              data-testid="save-btn"
              className="w-full h-14 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-lg font-semibold gap-2"
            >
              <Save className="w-5 h-5" /> {saving ? "Saving..." : isEdit ? "Save Changes" : "Add Customer"}
            </Button>

            {isEdit && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    data-testid="delete-btn"
                    className="w-full h-12 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-xl font-semibold gap-2"
                  >
                    <Trash2 className="w-5 h-5" /> Delete Customer
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="max-w-sm rounded-2xl">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete this customer?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This action cannot be undone. All notes and follow-up reminders will be removed.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="cancel-delete" className="rounded-xl">Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      data-testid="confirm-delete"
                      onClick={onDelete}
                      className="bg-red-600 hover:bg-red-700 rounded-xl"
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </form>
        )}
      </div>
    </>
  );
}
